import React from 'react';

import { defineMessages, type IntlShape } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/add';
import AIAgentIcon from '@atlaskit/icon/core/ai-agent';
import AutomationIcon from '@atlaskit/icon/core/automation';
import { type AIAction } from '@atlassian/ai-smart-button-api';

export type StudioAction = {
	id: string;
	title: string;
	description?: string;
	icon: React.ReactElement;
	onClick?: () => void;
	href?: string;
};
export const i18n = defineMessages({
	aiStudioCreateAgentAction: {
		id: 'ai-smart-button.ai-actions.rovo-agents.create-agent.text',
		defaultMessage: 'Create agent',
		description: 'The action to create a new agent in the AI Studio',
	},
	aiStudioBrowseAgentAction: {
		id: 'ai-smart-button.ai-actions.rovo-agents.create-agent.text',
		defaultMessage: 'Browse agents',
		description: 'The action to create a new agent in the AI Studio',
	},
	aiStudioAutomationsAction: {
		id: 'ai-smart-button.ai-actions.rovo-agents.automations.text',
		defaultMessage: 'Automations',
		description: 'The action to open Automations in the AI Studio',
	},
	aiStudioCreateAutomationAction: {
		id: 'ai-smart-button.ai-actions.rovo-agents.create-automation.text',
		defaultMessage: 'Create automation',
		description: 'The action to open Create Automation in the AI Studio',
	},
	addIconLabel: {
		id: 'ai-smart-button.ai-actions.rovo-agents.add-icon-label',
		defaultMessage: 'Add icon',
		description: 'Label for the add icon',
	},
	agentIconLabel: {
		id: 'ai-smart-button.ai-actions.rovo-agents.agent-icon-label',
		defaultMessage: 'Agent icon',
		description: 'Label for the agent icon',
	},
	automationIconLabel: {
		id: 'ai-smart-button.ai-actions.rovo-agents.automation-icon-label',
		defaultMessage: 'Automation icon',
		description: 'Label for the automation icon',
	},
});

export function getStudioActions({
	intl,
	createAgentUrl,
	handleBrowseAgent,
	renderUserAutomations,
	onCreateAutomation,
}: {
	intl: IntlShape;
	createAgentUrl: string;
	handleBrowseAgent: () => void;
	renderUserAutomations?: () => React.ReactElement;
	onCreateAutomation?: () => void;
}): (StudioAction | AIAction)[] {
	return [
		{
			id: 'create-agent',
			title: intl.formatMessage(i18n.aiStudioCreateAgentAction),
			href: createAgentUrl,
			icon: <AddIcon label={intl.formatMessage(i18n.addIconLabel)} />,
		},
		{
			id: 'browse-agent',
			title: intl.formatMessage(i18n.aiStudioBrowseAgentAction),
			onClick: handleBrowseAgent,
			icon: <AIAgentIcon label={intl.formatMessage(i18n.agentIconLabel)} />,
		},
		...(onCreateAutomation
			? [
					{
						id: 'create-automations',
						title: intl.formatMessage(i18n.aiStudioCreateAutomationAction),
						icon: <AddIcon label="" />,
						onClick: onCreateAutomation,
					},
				]
			: []),
		...(renderUserAutomations
			? [
					{
						id: 'user-automations',
						title: intl.formatMessage(i18n.aiStudioAutomationsAction),
						icon: <AutomationIcon label={intl.formatMessage(i18n.automationIconLabel)} />,
						viewConfig: {
							render: renderUserAutomations,
						},
					},
				]
			: []),
	];
}
